/**
 * React Query hooks for the unified Finalized tab — paginated list +
 * tab-aggregated actuals KPI. Mirrors the useCreatorsTab pair; same
 * staleTime, same queryKey shape, separate keys so SSE / mutation
 * invalidation targets one tab cleanly.
 *
 * The Finalized KPI shape differs from Creators (actual_views +
 * locked_budget + live_creator_count vs estimated_reach + estimated_budget),
 * but the hook contract stays identical — one list query, one KPI query.
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import type { FinalizedTabKPI, RosterCounts, RosterEntryFinalized } from '@discovr/types';

import {
  createCampaignDetailApi,
  type UnifiedListParams,
} from '../api/campaignQueries';
import type { PagedResult } from '../api/shared';
import { campaignKeys } from '../queryKeys';

const api = createCampaignDetailApi();

/**
 * Paginated Finalized-tab list. `params` defaults match the BE
 * (page=1, limit=20). The `phase` sub-filter (script | content | live) is
 * forwarded to the BE so the sub-tab is filtered + paginated server-side —
 * total/totalPages then reflect the phase, not the whole finalized set.
 *
 * `keepPreviousData` holds the current page visible while the next page /
 * sub-tab loads, so paging and sub-tab switches don't flash a skeleton.
 */
export function useFinalizedTab(
  campaignId: string,
  params: UnifiedListParams = {},
) {
  return useQuery<PagedResult<RosterEntryFinalized>>({
    queryKey: campaignKeys.rosterTab(campaignId, 'finalized', params),
    queryFn: () => api.listFinalized(campaignId, params),
    enabled: !!campaignId,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Consolidated per-status + per-phase roster counts. One query powers every
 * count badge on both the Creators tab (pending / approved / rejected / all)
 * and the Finalized tab (all / script / content / live) — replacing the
 * Creators tab's four limit:1 count queries. Server-computed across the whole
 * tab, so badges stay correct regardless of the current page.
 *
 * `options.enabled` lets a caller suppress the fetch outright (e.g. the
 * public read-only share view — a public link never carries campaign-wide
 * counts). Same `enabled: !!campaignId && (options?.enabled ?? true)`
 * precedent as useLastImportResult. Defaults to enabled so every existing
 * caller is unchanged.
 */
export function useRosterCounts(campaignId: string, options?: { enabled?: boolean }) {
  return useQuery<RosterCounts>({
    queryKey: campaignKeys.rosterCounts(campaignId),
    queryFn: () => api.getRosterCounts(campaignId),
    enabled: !!campaignId && (options?.enabled ?? true),
    staleTime: 30_000,
  });
}

/**
 * Finalized-tab actuals KPI — actual_views (live rows only),
 * locked_budget (all finalized rows), creator_count + live_creator_count.
 * Scope fixed per tab — no ?status= filter.
 *
 * `options.enabled` lets a caller suppress the fetch outright (e.g. the
 * public read-only share view — a public link never carries a campaign-wide
 * actuals aggregate) — same `enabled: !!campaignId && (options?.enabled ??
 * true)` precedent as useCreatorsKPI / useLastImportResult. Defaults to
 * enabled so every existing caller is unchanged.
 */
export function useFinalizedKPI(campaignId: string, options?: { enabled?: boolean }) {
  return useQuery<FinalizedTabKPI>({
    queryKey: campaignKeys.rosterKpisFinalized(campaignId),
    queryFn: () => api.getFinalizedKPI(campaignId),
    enabled: !!campaignId && (options?.enabled ?? true),
    staleTime: 30_000,
  });
}
